/**
 * The email newsletter's subscriber list (newsletter-subscribers, 2026-10-24;
 * build-output/architecture/email-campaigns.md → "Subscribers").
 *
 * Mario (24 Oct): "I want to see who is in the list, when they joined", on
 * `/campaigns`, newest first, each name opening the member's page. The
 * newsletter is opt-out (Mario, 24 Oct: keep "everyone it reaches"): every
 * active member it can reach is on it, whether or not they ever used a sign-up
 * form. So "joined" has two readings, and {@link newsletterJoin} is the one
 * rule that picks between them, read by the list and by the member page's
 * Newsletter row alike:
 *
 *   - someone who signed up (a newsletter form, or staff's Re-subscribe — every
 *     completed `resubscribe_newsletter`) joined at their latest sign-up,
 *     `members.newsletter_signup_at` (migration 246);
 *   - everyone else joined when they became a member — their first record,
 *     `LEAST(created_at, first_known_at)` (`memberFirstKnownSql`).
 *
 * Who is ON the list is not decided here: that is the newsletter's own reach
 * rule, `classifyEmailNewsletterReach`, so the list can never disagree with
 * the send.
 *
 * Pure, and it imports only `marketing-audience.ts`, so the dashboard can
 * bundle it.
 */

import type { MarketingExclusionReason } from "./marketing-audience.js";

/**
 * How a member came to be on the list:
 *   - `newsletter_form` — their latest sign-up is the newsletter form that
 *     created their record (`members.signup_source = 'newsletter'`, migration
 *     241, and the sign-up's time is their first record's);
 *   - `signed_up` — they signed up while already on file: a newsletter form
 *     that matched their record, or staff's Re-subscribe — including someone a
 *     form created who signed up again later;
 *   - `member` — they never signed up; the newsletter reaches them as a member.
 */
export const NEWSLETTER_JOINED_VIA = ["newsletter_form", "signed_up", "member"] as const;
export type NewsletterJoinedVia = (typeof NEWSLETTER_JOINED_VIA)[number];

/** The `signup_source` a newsletter form writes on the record it creates. */
export const NEWSLETTER_SIGNUP_SOURCE = "newsletter";

/** What {@link newsletterJoin} reads, as the member row holds it. */
export interface NewsletterJoinFacts {
  /** `members.newsletter_signup_at` — the latest sign-up; null when they never signed up. */
  readonly newsletterSignupAt: Date | null;
  /** `members.signup_source`. */
  readonly signupSource: string | null;
  /** Their first record, `LEAST(created_at, first_known_at)`. */
  readonly firstKnownAt: Date;
}

export interface NewsletterJoin {
  readonly at: Date;
  readonly via: NewsletterJoinedVia;
}

/**
 * When and how the member joined the newsletter. A form-created record's
 * sign-up time is the row's own `created_at` (`member_self_signup`), and an
 * absorb carries `signup_source` together with the first record's date, so
 * the latest sign-up IS the one that created the record exactly when the two
 * times are equal; a later sign-up (the form again, or staff) is `signed_up`.
 */
export function newsletterJoin(facts: NewsletterJoinFacts): NewsletterJoin {
  if (facts.newsletterSignupAt === null) return { at: facts.firstKnownAt, via: "member" };
  const formMadeTheRecord =
    facts.signupSource === NEWSLETTER_SIGNUP_SOURCE &&
    facts.newsletterSignupAt.getTime() === facts.firstKnownAt.getTime();
  return { at: facts.newsletterSignupAt, via: formMadeTheRecord ? "newsletter_form" : "signed_up" };
}

/**
 * Which members the list shows:
 *   - `on_list` — everyone the newsletter reaches (the default);
 *   - `signed_up` — everyone who signed up ({@link NewsletterJoinedVia} other
 *     than `member`), on the list or not;
 *   - `left_out` — active members the newsletter does not reach, each with the
 *     reach rule's reason.
 */
export const NEWSLETTER_SUBSCRIBER_VIEWS = ["on_list", "signed_up", "left_out"] as const;
export type NewsletterSubscriberView = (typeof NEWSLETTER_SUBSCRIBER_VIEWS)[number];

/** The longest search the list takes. */
export const NEWSLETTER_SUBSCRIBER_SEARCH_MAX_CHARS = 100;
/** Rows per page, by default and at most. */
export const NEWSLETTER_SUBSCRIBERS_DEFAULT_LIMIT = 50;
export const NEWSLETTER_SUBSCRIBERS_MAX_LIMIT = 200;
/** The furthest offset the list pages to. */
export const NEWSLETTER_SUBSCRIBERS_MAX_OFFSET = 100_000;

/** One member as the list's filter, search and order read them. */
export interface NewsletterSubscriberCandidate {
  readonly memberId: string;
  readonly displayName: string | null;
  readonly email: string | null;
  readonly join: NewsletterJoin;
  /** The reach rule's verdict: null = on the list. */
  readonly leftOutReason: MarketingExclusionReason | null;
}

/** Is the member in this view? */
export function inNewsletterSubscriberView(
  c: NewsletterSubscriberCandidate,
  view: NewsletterSubscriberView,
): boolean {
  switch (view) {
    case "on_list":
      return c.leftOutReason === null;
    case "signed_up":
      return c.join.via !== "member";
    case "left_out":
      return c.leftOutReason !== null;
  }
}

/**
 * The search as matched: trimmed and lower case; null when it is empty, which
 * matches everyone.
 */
export function normalizeNewsletterSubscriberSearch(search: string | undefined): string | null {
  const s = (search ?? "").trim().toLowerCase();
  return s.length === 0 ? null : s;
}

/** Does the member's name or email contain the (normalized) search? */
export function matchesNewsletterSubscriberSearch(
  c: Pick<NewsletterSubscriberCandidate, "displayName" | "email">,
  search: string | null,
): boolean {
  if (search === null) return true;
  return (
    (c.displayName ?? "").toLowerCase().includes(search) || (c.email ?? "").toLowerCase().includes(search)
  );
}

/** Newest joiner first; the member id breaks a tie, so a page never shifts between reads. */
export function compareNewsletterSubscribers(
  a: Pick<NewsletterSubscriberCandidate, "memberId" | "join">,
  b: Pick<NewsletterSubscriberCandidate, "memberId" | "join">,
): number {
  const byJoin = b.join.at.getTime() - a.join.at.getTime();
  if (byJoin !== 0) return byJoin;
  return a.memberId < b.memberId ? -1 : a.memberId > b.memberId ? 1 : 0;
}

/** The counts above the list. "Joined" counts only members on the list. */
export interface NewsletterSubscriberTotals {
  readonly active_members: number;
  readonly on_list: number;
  readonly left_out: number;
  readonly signed_up: number;
  readonly joined_last_7_days: number;
  readonly joined_last_30_days: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function countNewsletterSubscribers(
  candidates: ReadonlyArray<NewsletterSubscriberCandidate>,
  asOf: Date,
): NewsletterSubscriberTotals {
  let onList = 0;
  let signedUp = 0;
  let joined7 = 0;
  let joined30 = 0;
  for (const c of candidates) {
    if (c.join.via !== "member") signedUp += 1;
    if (c.leftOutReason !== null) continue;
    onList += 1;
    const age = asOf.getTime() - c.join.at.getTime();
    if (age < 7 * DAY_MS) joined7 += 1;
    if (age < 30 * DAY_MS) joined30 += 1;
  }
  return {
    active_members: candidates.length,
    on_list: onList,
    left_out: candidates.length - onList,
    signed_up: signedUp,
    joined_last_7_days: joined7,
    joined_last_30_days: joined30,
  };
}
